/**
 * NotificationList Component
 * 
 * A dropdown panel for displaying notifications with severity icons, read/unread status,
 * and action buttons for managing notifications.
 * 
 * Features:
 * - Displays notifications with severity icons
 * - Shows read/unread visual distinction
 * - Provides action buttons (mark as read, delete)
 * - Includes bulk actions (mark all as read, clear all)
 * - Handles empty state for no notifications
 * 
 * @module NotificationList
 */

import React, { useState, useEffect, useRef } from 'react';
import NotificationService from '../../services/NotificationService';

/**
 * NotificationList component for displaying notifications
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the notification list is open
 * @param {Function} props.onClose - Function to call when the list should be closed
 * @returns {JSX.Element} NotificationList component
 */
function NotificationList({ isOpen, onClose }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const notificationService = useRef(new NotificationService());
  const listRef = useRef(null);

  /**
   * Load notifications from the service
   */
  const loadNotifications = async () => {
    try {
      setLoading(true);
      const userNotifications = await notificationService.current.getUserNotifications();
      
      // Sort notifications by timestamp (newest first) and read status (unread first)
      const sortedNotifications = userNotifications.sort((a, b) => {
        if (a.read !== b.read) return a.read ? 1 : -1;
        return b.timestamp - a.timestamp;
      });
      
      setNotifications(sortedNotifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Mark a notification as read
   * @param {string} id - Notification ID
   */
  const handleMarkAsRead = async (id) => {
    try {
      await notificationService.current.markAsRead(id);
      // Update the local state
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => 
          notification.id === id ? { ...notification, read: true } : notification
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  /**
   * Delete a notification
   * @param {string} id - Notification ID
   */
  const handleDelete = async (id) => {
    try {
      await notificationService.current.deleteNotification(id);
      // Update the local state
      setNotifications(prevNotifications => 
        prevNotifications.filter(notification => notification.id !== id)
      );
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  /**
   * Mark all notifications as read
   */
  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.current.markAllAsRead();
      // Update the local state
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => ({ ...notification, read: true }))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  /**
   * Delete all notifications
   */
  const handleClearAll = async () => {
    try {
      await notificationService.current.deleteAllNotifications();
      // Update the local state
      setNotifications([]);
    } catch (error) {
      console.error('Error clearing all notifications:', error);
    }
  };

  /**
   * Get severity icon class
   * @param {string} severity - Notification severity
   * @returns {string} CSS class for severity icon
   */
  const getSeverityIconClass = (severity) => {
    switch (severity) {
      case 'Critical':
        return 'severity-icon-critical';
      case 'Warning':
        return 'severity-icon-warning';
      case 'Info':
      default:
        return 'severity-icon-info';
    }
  };

  /**
   * Format timestamp to readable date/time
   * @param {number} timestamp - Timestamp in milliseconds
   * @returns {string} Formatted date/time
   */
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Load notifications when the component mounts or the isOpen state changes
  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  // Handle clicks outside the notification list
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (listRef.current && !listRef.current.contains(event.target) && isOpen) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Subscribe to notification events
  useEffect(() => {
    // Create a BroadcastChannel for notification updates
    const channel = new BroadcastChannel('notification-updates');
    
    // Listen for notification updates
    const handleNotificationUpdate = () => {
      if (isOpen) {
        loadNotifications();
      }
    };
    
    channel.addEventListener('message', handleNotificationUpdate);
    
    // Clean up event listener on unmount
    return () => {
      channel.removeEventListener('message', handleNotificationUpdate);
      channel.close();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="notification-list-container" ref={listRef} role="dialog" aria-label="Notifications">
      <div className="notification-list-header">
        <h3>Notifications</h3>
        <button className="close-button" onClick={onClose} aria-label="Close notifications">
          &times;
        </button>
      </div>
      
      <div className="notification-list-actions">
        <button 
          className="action-button" 
          onClick={handleMarkAllAsRead}
          disabled={notifications.length === 0 || notifications.every(n => n.read)}
        >
          Mark all as read
        </button>
        <button 
          className="action-button" 
          onClick={handleClearAll}
          disabled={notifications.length === 0}
        >
          Clear all
        </button>
      </div>
      
      <div className="notification-list-content">
        {loading ? (
          <div className="loading-indicator">Loading notifications...</div>
        ) : notifications.length === 0 ? (
          <div className="empty-state">No notifications</div>
        ) : (
          notifications.map(notification => (
            <div 
              key={notification.id} 
              className={`notification-item ${notification.read ? 'read' : 'unread'}`}
            >
              <div className="notification-icon">
                <span className={`severity-icon ${getSeverityIconClass(notification.severity)}`} />
              </div>
              <div className="notification-content">
                <div className="notification-title">{notification.title}</div>
                <div className="notification-message">{notification.message}</div>
                <div className="notification-timestamp">{formatTimestamp(notification.timestamp)}</div>
              </div>
              <div className="notification-actions">
                {!notification.read && (
                  <button 
                    className="notification-action-button"
                    onClick={() => handleMarkAsRead(notification.id)}
                    aria-label="Mark as read"
                  >
                    ✓
                  </button>
                )}
                <button 
                  className="notification-action-button"
                  onClick={() => handleDelete(notification.id)}
                  aria-label="Delete notification"
                >
                  ×
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default NotificationList;
